import React from 'react';
import { resolveSx, splitSystemProps } from './style-utils.js';
import './palette.less';

export interface BoxProps extends React.HTMLAttributes<HTMLElement> {
  as?: React.ElementType;
  component?: React.ElementType;
  sx?: React.CSSProperties | Record<string, unknown>;
}

// Minimal layout primitive replacing @mui/material Box for the common cases.
// Supports the sx-lite subset handled by resolveSx (spacing shorthands on the
// 8px scale, palette tokens, responsive breakpoint objects, plain CSS keys)
// both via sx and as direct system props; pseudo-selectors are not supported
// and should be expressed in CSS instead.
export const Box = React.forwardRef<HTMLElement, BoxProps>(({
  as,
  component,
  sx,
  style,
  className,
  children,
  ...props
}, ref) => {
  const Element: React.ElementType = component || as || 'div';
  const { system, rest } = splitSystemProps(props);
  const resolved = resolveSx(system, sx);
  const resolvedStyle = { ...resolved.style, ...style };
  const resolvedClassName = [className, resolved.className].filter(Boolean).join(' ') || undefined;

  return (
    <Element
      ref={ref}
      className={resolvedClassName}
      style={Object.keys(resolvedStyle).length ? resolvedStyle : undefined}
      {...rest}
    >
      {children}
    </Element>
  );
});

Box.displayName = 'Box';

export default Box;
